import { type ReactNode, useRef, useState } from "react";

import { type CheckboxChangeEvent } from "antd/lib/checkbox";

import {
  BuiCheckbox,
  type IBuiCheckboxProps,
} from "@btnet/blueprint-components/BuiCheckbox";
import { cn } from "@btnet/utilities-core";

import {
  BTAnalyticsEvent,
  BTTrack,
  type IBTTrackingProps,
  trackBTEvent,
} from "../analytics/analytics";
import { BTPopover } from "../BTPopover/BTPopover";
import { resolveHotkey, type THotkey } from "../Hotkey/hotkey.types";
import { useHotkey } from "../Hotkey/useHotkey";
import { type KeyOfOrString } from "../types/KeyOfOrString.types";

export interface IBTCheckboxProps<FormValues>
  extends Omit<IBuiCheckboxProps, "id"> {
  id: KeyOfOrString<FormValues>;
  readOnly?: boolean;
  hotkey?: THotkey;
  onHotkeyPressed?: (e: CheckboxChangeEvent) => void;
  analyticId?: string;
  /** the tooltip text or rich text node */
  tooltip?: ReactNode;
}

export const BTCheckbox = BTTrack({ atom: "BTCheckbox" })(function <
  FormValues = undefined,
>({
  id,
  readOnly,
  "data-testid": testid,
  hotkey,
  trackingEvent,
  tracking,
  analyticId = "BTCheckbox",
  className,
  onChange,
  disabled,
  onHotkeyPressed,
  tabIndex,
  children,
  checked,
  indeterminate,
  defaultChecked,
  tooltip,
  ...otherProps
}: IBTCheckboxProps<FormValues> & IBTTrackingProps) {
  const localDisabled = Boolean(readOnly || disabled);

  const ref = useRef<HTMLInputElement>();

  const [isChecked, setIsChecked] = useState(
    defaultChecked ?? checked ?? false,
  );

  // Have to keep state in sync with props, this could be improved in a future iteration
  if (checked !== undefined && checked !== isChecked) {
    setIsChecked(checked ?? false);
  }
  // AntD is overriding how useRef works, so we need to use this any. 😠
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const setRefCurrent = (node: any) => {
    if (node && node.input) {
      ref.current = node.input;
    } else {
      ref.current = undefined;
    }
  };
  const checkboxChangeEvent: CheckboxChangeEvent = {
    target: {
      checked: true,
    },
    nativeEvent: new MouseEvent("submit"),
    stopPropagation: () => {},
    preventDefault: () => {},
  };

  // Use this if you want to pass a different function in from the normal checkbox
  const handleHotkeyPressed = (e: CheckboxChangeEvent) => {
    if (localDisabled) {
      return;
    }

    if (onHotkeyPressed) {
      setIsChecked(e.target.checked);
      onHotkeyPressed(e);
    } else {
      handleChange(e);
    }
    trackBTEvent(tracking, {
      element: "BTCheckbox",
      event: trackingEvent ?? BTAnalyticsEvent.ValueChange,
      analyticId,
      testid: testid,
      extraInfo: {
        checked: e.target.checked,
      },
    });
  };

  // Core's useHotkey takes no element, so the ctrl+/ overlay no longer anchors a tooltip to
  // this checkbox — the key is still listed, just without the positioned bubble.
  const resolvedHotkey = resolveHotkey(hotkey, id as string);
  useHotkey({
    command: resolvedHotkey?.command ?? "",
    friendlyName: resolvedHotkey?.friendlyName,
    onCommand: () => handleHotkeyPressed(checkboxChangeEvent),
    disabled: readOnly || !resolvedHotkey,
  });

  const handleChange = (e: CheckboxChangeEvent) => {
    if (localDisabled) {
      return;
    }
    setIsChecked(e.target.checked);
    if (onChange) {
      onChange(e);
    }
    trackBTEvent(tracking, {
      element: "BTCheckbox",
      event: trackingEvent ?? BTAnalyticsEvent.ValueChange,
      analyticId,
      testid: testid,
      extraInfo: {
        checked: e.target.checked,
      },
    });
  };

  const labelId =
    id !== undefined && id !== null ? `${id as string}label` : undefined;

  return (
    <BuiCheckbox
      {...otherProps}
      className={cn("BTCheckbox", className, {
        readOnly: readOnly,
        disabled: localDisabled,
        "underline decoration-dotted cursor-pointer pointer-events-auto":
          tooltip && children,
      })}
      id={id as string}
      onChange={handleChange}
      tabIndex={localDisabled ? -1 : (tabIndex ?? 0)}
      aria-disabled={localDisabled}
      ref={setRefCurrent}
      data-testid={testid}
      indeterminate={indeterminate ?? false}
      checked={isChecked}
      defaultChecked={defaultChecked ?? false}
      // Only emit aria-labelledby when there's a real label element to point at;
      // exactOptionalPropertyTypes forbids passing undefined/null to the optional prop.
      {...(children && labelId ? { "aria-labelledby": labelId } : {})}
    >
      <BTPopover
        data-testid={testid + "-tooltip"}
        content={tooltip}
        disabled={!tooltip || !children}
      >
        {children}
      </BTPopover>
    </BuiCheckbox>
  );
});
